import { useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';

// Auto-logout after IDLE_LIMIT_MS of no interaction. Rendered once at the
// app-routes level (inside AuthProvider). Gated on `authed`.
//
// Why localStorage instead of a plain timer: a background/asleep tab throttles
// setInterval, so we compare wall-clock against a persisted last-activity
// stamp rather than trusting a timer to fire. The stamp is shared across tabs
// (active in one tab keeps the others alive) and survives reloads — including
// the seamless auto-update reload — so idle time isn't reset behind the user's
// back. auth.login() reseeds it to now, so every fresh sign-in gets a clean hour.
export const IDLE_LIMIT_MS = 60 * 60 * 1000; // 1 hour
// Exported: a long-running operation the user started (a blast send) is NOT
// idleness, so it re-stamps this key to hold the logout off. See BlastPanel.
export const LAST_ACTIVITY_KEY = '3c_last_activity';
const LAST_KEY = LAST_ACTIVITY_KEY;
const CHECK_EVERY_MS = 30 * 1000;
const WRITE_THROTTLE_MS = 20 * 1000; // don't hammer localStorage on mousemove/scroll

export default function IdleLogout() {
  const { authed, logout } = useAuth();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const lastWrite = useRef(0);

  useEffect(() => {
    if (!authed) return undefined;
    // Seed a stamp if none exists; do NOT reset an existing one, so idle time
    // carries across reloads. (login() sets a fresh stamp on real sign-in.)
    try { if (!localStorage.getItem(LAST_KEY)) localStorage.setItem(LAST_KEY, String(Date.now())); } catch (_) {}

    const readLast = () => {
      try { return Number(localStorage.getItem(LAST_KEY)) || Date.now(); }
      catch (_) { return Date.now(); }
    };

    function bump() {
      const t = Date.now();
      if (t - lastWrite.current < WRITE_THROTTLE_MS) return; // throttle writes
      lastWrite.current = t;
      try { localStorage.setItem(LAST_KEY, String(t)); } catch (_) {}
    }
    function check() {
      if (Date.now() - readLast() >= IDLE_LIMIT_MS) {
        try { localStorage.removeItem(LAST_KEY); } catch (_) {}
        try { logoutRef.current(); } catch (_) {}
      }
    }
    function onFocus() { if (document.visibilityState !== 'hidden') check(); }

    const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'scroll', 'wheel'];
    EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const iv = setInterval(check, CHECK_EVERY_MS);
    check(); // immediate: reopening a tab that's been idle past the limit logs out at once

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, bump));
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      clearInterval(iv);
    };
  }, [authed]);

  return null;
}
